import { useState, useEffect, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { fetchEmployees } from "@/lib/employees";
import { fetchJSON } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Trash2, Edit, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { formatDate, formatDateTime } from "@/lib/dateUtils";

interface SwipeItem {
  id: string;
  employee: string;
  swipeMode: string;
  batchNo: string;
  amount: string;
  note: string;
  uploadedFile: string | null;
}

export default function SwipeSale() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State for global form fields
  const [saleDate, setSaleDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [shift, setShift] = useState<"S-1" | "S-2">("S-1");
  const [totalValue, setTotalValue] = useState<string>("");

  // Auto-fill
  const [autoFillEmployee, setAutoFillEmployee] = useState<string>("");
  const [autoFillSwipeType, setAutoFillSwipeType] = useState<string>(""); // Card/UPI

  // Dynamic Entry Rows
  const [swipeItems, setSwipeItems] = useState<SwipeItem[]>([{
    id: "1",
    employee: "",
    swipeMode: "",
    batchNo: "",
    amount: "",
    note: "",
    uploadedFile: null
  }]);

  // Filters
  const [searchFrom, setSearchFrom] = useState<string>("");
  const [searchTo, setSearchTo] = useState<string>("");
  const [filterSwipeMode, setFilterSwipeMode] = useState<string>("all");
  const [searchText, setSearchText] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);

  // Fetch employees
  const { data: employeesData } = useQuery({
    queryKey: ["/api/employees"],
    queryFn: fetchEmployees,
  });

  // Fetch swipe machines
  const { data: swipeMachinesData } = useQuery({
    queryKey: ["/api/swipe-machines"],
    queryFn: async () => {
      const response = await fetchJSON("/api/swipe-machines");
      const result = await response.json();
      if (!response.ok || !result.ok) throw new Error(result.error || "Failed to fetch swipe machines");
      return result.rows || [];
    },
  });

  // Fetch swipe transactions
  const { data: swipeTransactionsData, refetch } = useQuery({
    queryKey: ["/api/swipe-transactions", searchFrom, searchTo, filterSwipeMode],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterSwipeMode && filterSwipeMode !== "all") params.set("mode", filterSwipeMode);
      if (searchFrom) params.set("from_date", searchFrom);
      if (searchTo) params.set("to_date", searchTo);
      const qs = params.toString();
      const response = await fetchJSON(`/api/swipe-transactions${qs ? `?${qs}` : ""}`);
      const result = await response.json();
      if (!response.ok || !result.ok) throw new Error(result.error || "Failed to fetch swipe transactions");
      return result.rows || [];
    },
  });

  const swipeTransactionsRows = swipeTransactionsData || [];
  const employees = employeesData || [];
  const swipeMachines = swipeMachinesData || [];

  /** Machine names from master, or Card/UPI/QR when no machines configured */
  const swipeModeOptions = useMemo(() => {
    const baseOptions = [
      { value: "Card", label: "Card" },
      { value: "UPI", label: "UPI" },
      { value: "QR", label: "QR" },
    ];
    const active = swipeMachines.filter(
      (m: { status?: string }) => !m.status || m.status === "Active"
    );
    const machineOptions = active.map((m: { id?: string; machine_name: string }) => ({
        value: m.machine_name,
        label: m.machine_name,
      }));
    return [...baseOptions, ...machineOptions].filter((option, index, options) =>
      options.findIndex((candidate) => candidate.value === option.value) === index
    );
  }, [swipeMachines]);

  const handleAutoFillEmployee = (val: string) => {
    setAutoFillEmployee(val);
    setSwipeItems(prev => prev.map(item => ({ ...item, employee: val })));
  };

  const handleAutoFillSwipeType = (val: string) => {
    setAutoFillSwipeType(val);
    setSwipeItems(prev => prev.map(item => ({ ...item, swipeMode: val })));
  };

  // Total Value Sync
  useEffect(() => {
    const sum = swipeItems.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0);
    setTotalValue(sum > 0 ? sum.toFixed(2) : "");
  }, [swipeItems]);

  const updateItem = (id: string, field: keyof SwipeItem, value: any) => {
    setSwipeItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      return { ...item, [field]: value };
    }));
  };

  const handleAddItem = () => {
    setSwipeItems(prev => [...prev, {
      id: Date.now().toString(),
      employee: autoFillEmployee || "",
      swipeMode: autoFillSwipeType || "",
      batchNo: "",
      amount: "",
      note: "",
      uploadedFile: null
    }]);
  };

  const handleRemoveItem = (id: string) => {
    if (swipeItems.length > 1) {
      setSwipeItems(prev => prev.filter(item => item.id !== id));
    }
  };

  const handleFileUpload = (id: string, file: File | undefined) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateItem(id, 'uploadedFile', reader.result as string);
        toast({ title: "File uploaded successfully" });
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = (id: string) => {
    const input = document.getElementById(`file-upload-${id}`) as HTMLInputElement;
    if (input) input.click();
  };

  const buildPayload = (item: SwipeItem) => {
    const employeeId = item.employee || autoFillEmployee;
    const swipeMode = item.swipeMode || autoFillSwipeType;
    const amount = parseFloat(item.amount);
    return {
      employee_id: employeeId,
      swipe_type: autoFillSwipeType || swipeMode || "Card",
      swipe_mode: swipeMode,
      batch_number: item.batchNo || "0",
      amount,
      transaction_date: saleDate,
      shift,
      note: item.note,
      image_url:
        item.uploadedFile && item.uploadedFile.length < 500_000
          ? item.uploadedFile
          : undefined,
    };
  };

  const resetEntryForm = () => {
    setEditingId(null);
    setSwipeItems([
      {
        id: Date.now().toString(),
        employee: autoFillEmployee || "",
        swipeMode: autoFillSwipeType || "",
        batchNo: "",
        amount: "",
        note: "",
        uploadedFile: null,
      },
    ]);
  };

  const createSwipeTransaction = useMutation({
    mutationFn: async () => {
      if (editingId) {
        const item = swipeItems[0];
        const response = await fetchJSON(`/api/swipe-transactions/${editingId}`, {
          method: "PUT",
          body: JSON.stringify(buildPayload(item)),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.ok) {
          throw new Error(result.error || `Update failed (${response.status})`);
        }
        return true;
      }

      for (const item of swipeItems) {
        const response = await fetchJSON("/api/swipe-transactions", {
          method: "POST",
          body: JSON.stringify(buildPayload(item)),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.ok) {
          throw new Error(result.error || `Save failed (${response.status})`);
        }
      }
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/swipe-transactions"] });
      toast({
        title: editingId ? "Updated successfully" : "Saved successfully",
        className: "bg-green-500 text-white",
      });
      resetEntryForm();
      refetch();
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
  });

  const deleteSwipeTransactions = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        const response = await fetchJSON(`/api/swipe-transactions/${id}`, {
          method: "DELETE",
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.ok) {
          throw new Error(result.error || `Delete failed (${response.status})`);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/swipe-transactions"] });
      toast({ title: "Deleted successfully", className: "bg-green-500 text-white" });
      setSelectedIds(new Set());
      if (editingId) resetEntryForm();
      refetch();
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
  });

  const handleEditRow = (row: Record<string, unknown>) => {
    const id = String(row.id ?? "");
    if (!id) return;

    setEditingId(id);
    const txDate = row.transaction_date
      ? new Date(String(row.transaction_date))
      : new Date();
    if (!Number.isNaN(txDate.getTime())) {
      setSaleDate(txDate.toISOString().slice(0, 10));
    }
    const rowShift = String(row.shift || "S-1");
    if (rowShift === "S-1" || rowShift === "S-2") setShift(rowShift);

    const empId = String(row.employee_id || "");
    setAutoFillEmployee(empId);
    setAutoFillSwipeType(String(row.swipe_type || ""));

    setSwipeItems([
      {
        id: "edit-row",
        employee: empId,
        swipeMode: String(row.swipe_mode || ""),
        batchNo: String(row.batch_number ?? ""),
        amount: String(row.amount ?? ""),
        note: String(row.note || ""),
        uploadedFile: (row.image_url as string) || null,
      },
    ]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteRow = (id: string) => {
    if (!confirm("Delete this swipe transaction?")) return;
    deleteSwipeTransactions.mutate([id]);
  };

  const handleBulkDelete = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast({
        title: "Nothing selected",
        description: "Select one or more rows to delete.",
        variant: "destructive",
      });
      return;
    }
    if (!confirm(`Delete ${ids.length} selected transaction(s)?`)) return;
    deleteSwipeTransactions.mutate(ids);
  };

  const toggleRowSelected = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(filteredRows.map((r: { id: string }) => r.id).filter(Boolean)));
  };

  const handleConfirm = () => {
    if (employees.length === 0) {
      toast({
        title: "No employees",
        description: "Add employees under Master → Employees first.",
        variant: "destructive",
      });
      return;
    }

    const invalid: string[] = [];
    swipeItems.forEach((item, i) => {
      const row = i + 1;
      if (!(item.employee || autoFillEmployee)) invalid.push(`Row ${row}: select employee`);
      const mode = item.swipeMode || autoFillSwipeType;
      if (!mode) invalid.push(`Row ${row}: select swipe mode or swipe type`);
      const amount = parseFloat(item.amount);
      if (!Number.isFinite(amount) || amount <= 0) invalid.push(`Row ${row}: enter amount greater than 0`);
    });

    if (invalid.length > 0) {
      toast({
        title: "Validation Error",
        description: invalid.slice(0, 3).join(". "),
        variant: "destructive",
      });
      return;
    }
    createSwipeTransaction.mutate();
  };

  const filteredRows = swipeTransactionsRows.filter((r: any) => {
    if (!searchText) return true;
    const s = searchText.toLowerCase();
    return (
      (r.employee_name && r.employee_name.toLowerCase().includes(s)) ||
      (r.swipe_mode && r.swipe_mode.toLowerCase().includes(s)) ||
      (r.amount && String(r.amount).includes(s))
    );
  });

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span className="font-semibold text-gray-700">Dashboard</span>
        <span>{'>'}</span>
        <span>Swipe</span>
      </div>

      {/* Main Card */}
      <Card className="border-none shadow-md overflow-hidden bg-blue-600">
        <div className="p-4">
          <h2 className="text-white text-xl font-semibold mb-6">Swipe</h2>

          {/* Top Row: Date, Shift, Total Value */}
          <div className="flex flex-wrap gap-4 items-center mb-6">
            <div className="flex items-center">
              <div className="bg-[#fbbf24] text-black font-medium px-4 py-2 rounded-l-md text-sm whitespace-nowrap">
                Choose Date
              </div>
              <Input
                type="date"
                className="rounded-l-none rounded-r-md border-none h-[36px] w-[180px]"
                value={saleDate}
                onChange={(e) => setSaleDate(e.target.value)}
              />
            </div>

            <div className="flex-1 bg-white/20 rounded-md p-2 flex items-center justify-center gap-6 border border-white/30 text-white min-w-[200px]">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="shift" checked={shift === 'S-1'} onChange={() => setShift('S-1')} className="accent-white w-4 h-4" />
                <span className="font-medium">S-1</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="shift" checked={shift === 'S-2'} onChange={() => setShift('S-2')} className="accent-white w-4 h-4" />
                <span className="font-medium">S-2</span>
              </label>
            </div>

            <div className="bg-white rounded-md h-[36px] w-[200px] flex items-center px-3 text-gray-700">
              {totalValue || "0"}
            </div>
          </div>

          {/* Auto Fill Container */}
          <div className="border border-white/30 rounded-lg p-4 mb-4 relative">
            <span className="absolute -top-3 left-4 bg-blue-600 px-2 text-white text-sm font-medium">AUTO-FILL</span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <Label className="text-white text-xs">Employee</Label>
                <Select value={autoFillEmployee} onValueChange={handleAutoFillEmployee}>
                  <SelectTrigger className="bg-white text-gray-700 border-none h-9">
                    <SelectValue placeholder="choose Employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((e: any) => (
                      <SelectItem key={e.id} value={e.id}>{e.employee_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-white text-xs">Swipe Type</Label>
                <Select value={autoFillSwipeType} onValueChange={handleAutoFillSwipeType}>
                  <SelectTrigger className="bg-white text-gray-700 border-none h-9">
                    <SelectValue placeholder="Choose Swipe" />
                  </SelectTrigger>
                  <SelectContent>
                    {swipeModeOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Dynamic Entry Rows */}
          <div className="space-y-4 mb-8">
            {swipeItems.map((item, index) => (
              <div key={item.id} className="grid grid-cols-12 gap-2 items-end border border-white/20 p-3 rounded-lg bg-blue-700/30">
                <div className="col-span-12 md:col-span-2">
                  <Label className="text-white text-xs mb-1 block">Employee</Label>
                  <Select value={item.employee} onValueChange={(val) => updateItem(item.id, 'employee', val)}>
                    <SelectTrigger className="bg-white text-gray-700 border-none h-9">
                      <SelectValue placeholder="Employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((e: any) => (
                        <SelectItem key={e.id} value={e.id}>{e.employee_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-12 md:col-span-2">
                  <Label className="text-white text-xs mb-1 block">Swipe Mode</Label>
                  <Select value={item.swipeMode} onValueChange={(val) => updateItem(item.id, 'swipeMode', val)}>
                    <SelectTrigger className="bg-white text-gray-700 border-none h-9">
                      <SelectValue placeholder="Swipe Mode" />
                    </SelectTrigger>
                    <SelectContent>
                      {swipeModeOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-6 md:col-span-1">
                  <Label className="text-white text-xs mb-1 block">Batch No</Label>
                  <Input className="h-9 bg-white border-none text-gray-700" placeholder="0" value={item.batchNo} onChange={e => updateItem(item.id, 'batchNo', e.target.value)} />
                </div>
                <div className="col-span-6 md:col-span-2">
                  <Label className="text-white text-xs mb-1 block">Amount</Label>
                  <Input className="h-9 bg-white border-none text-gray-700" placeholder="Amount" type="number" value={item.amount} onChange={e => updateItem(item.id, 'amount', e.target.value)} />
                </div>
                <div className="col-span-12 md:col-span-4 relative">
                  <div className="flex justify-between items-center mb-1">
                    <Label className="text-white text-xs">Note</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="bg-orange-500 hover:bg-orange-600 text-white h-5 text-[10px] px-2 rounded-sm"
                      onClick={() => triggerFileInput(item.id)}
                    >
                      UPLOAD
                    </Button>
                    <input id={`file-upload-${item.id}`} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload(item.id, e.target.files?.[0])} />
                  </div>
                  <Input className="h-9 bg-white border-none text-gray-700" placeholder="Note" value={item.note} onChange={e => updateItem(item.id, 'note', e.target.value)} />
                </div>
                <div className="col-span-12 md:col-span-1 flex justify-center gap-2 pb-1">
                  <Button size="icon" className="bg-transparent border border-white text-white hover:bg-white/20 rounded-full h-8 w-8" onClick={handleAddItem}>
                    <Plus className="h-5 w-5" />
                  </Button>
                  {swipeItems.length > 1 && (
                    <Button size="icon" className="bg-transparent border border-white text-white hover:bg-white/20 rounded-full h-8 w-8" onClick={() => handleRemoveItem(item.id)}>
                      <X className="h-5 w-5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Confirm Button */}
          <div className="flex justify-center">
            <Button
              className="bg-[#84cc16] hover:bg-[#65a30d] text-white font-bold px-12 rounded-full shadow-lg"
              onClick={handleConfirm}
              disabled={createSwipeTransaction.isPending}
            >
              {createSwipeTransaction.isPending
                ? editingId
                  ? "UPDATING..."
                  : "SAVING..."
                : editingId
                  ? "UPDATE"
                  : "CONFIRM"}
            </Button>
            {editingId && (
              <Button
                type="button"
                variant="outline"
                className="ml-3 bg-white text-blue-700"
                onClick={resetEntryForm}
              >
                Cancel edit
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Filter Bar */}
      <div className="bg-white rounded-lg p-4 shadow-sm flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">Search From</span>
            <Input type="date" className="w-[150px] h-9" value={searchFrom} onChange={e => setSearchFrom(e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">To</span>
            <Input type="date" className="w-[150px] h-9" value={searchTo} onChange={e => setSearchTo(e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">Swipe Mode</span>
            <Select value={filterSwipeMode} onValueChange={setFilterSwipeMode}>
              <SelectTrigger className="w-[160px] h-9">
                <SelectValue placeholder="Choose Swipe" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {swipeModeOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button className="bg-orange-500 hover:bg-orange-600 text-white h-9 px-6" onClick={() => refetch()}>
            <Search className="h-4 w-4 mr-2" /> Search
          </Button>
        </div>
      </div>

      {/* Controls & Table */}
      <div className="bg-white rounded-lg p-4 shadow-sm space-y-4">
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            className="text-red-500 border-red-200 bg-red-50 hover:bg-red-100 gap-2"
            onClick={handleBulkDelete}
            disabled={deleteSwipeTransactions.isPending}
          >
            <Trash2 className="h-4 w-4" /> Delete
            {selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
          </Button>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Filter:</span>
            <Input
              placeholder="Type to filter..."
              className="h-9 w-[200px]"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={
                      filteredRows.length > 0 &&
                      filteredRows.every((r: { id: string }) => selectedIds.has(r.id))
                    }
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                  />
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">S.No</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Employee</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Shift</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Swipe</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Amount</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Batch No</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Action</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Description</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Picture</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">User Log Details</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr><td colSpan={12} className="text-center py-8 text-gray-500">No records found</td></tr>
              ) : (
                filteredRows.map((row: any, i: number) => (
                  <tr key={row.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row.id)}
                        onChange={(e) => toggleRowSelected(row.id, e.target.checked)}
                      />
                    </td>
                    <td className="px-4 py-3">{i + 1}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {formatDate(row.transaction_date)}
                    </td>
                    <td className="px-4 py-3">
                      {row.employee_name || employees.find((e: any) => e.id === row.employee_id)?.employee_name || '-'}
                    </td>
                    <td className="px-4 py-3">{row.shift || 'S-1'}</td>
                    <td className="px-4 py-3">{row.swipe_mode}</td>
                    <td className="px-4 py-3 font-medium">{Number(row.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3">{row.batch_number}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="text-blue-500 hover:text-blue-700"
                          title="Edit"
                          onClick={() => handleEditRow(row)}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                          onClick={() => handleDeleteRow(row.id)}
                          disabled={deleteSwipeTransactions.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">{row.note || '-'}</td>
                    <td className="px-4 py-3">
                      {row.image_url ? (
                        <a href={row.image_url} target="_blank" rel="noreferrer" className="text-blue-500 underline text-xs">View</a>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      Created: Super Admin {formatDateTime(row.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
